/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { Other, Settings } from '.generated/Feature.CW.model';
import { LinkField, LinkFieldValue, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { CtaProps } from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';

import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';
import { CtaVariants } from 'lib/utils/style-param-utils/modules/ctas';
import { tv } from 'tailwind-variants';

export type PageSettings = Settings.SiteSettings['fields'];

interface ConditionTreatmentButton {
  url?: string;
  fields?: {
    Title?: {
      value?: string;
    };
  };
}

export type ButtonArrayProps = Other.ButtonArray &
  CtaProps & {
    params: {
      Theme: string;
      fieldSelector?: string;
    };
    isSidenavPart?: boolean;
  };

const ButtonArray = (props: ButtonArrayProps): JSX.Element => {
  const { base, wrapper, title, linkArray } = TailwindVariants();
  const buttons = props?.fields?.buttons;
  const ctaStyle = props?.fields?.buttonsVariant?.value?.toLowerCase() as CtaVariants;
  const isEditing = useIsEditing();
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext?.route;
  const fieldSelector = props?.params?.fieldSelector;
  const conditionButtons = contextWithSettings?.fields?.conditions;
  const treatmentsButtons = contextWithSettings?.fields?.treatments;

  const updatedButtons = buttons?.filter((button) => {
    const link = button?.fields?.link as LinkField;
    if (link?.value?.href !== '') {
      return true;
    }
    return false;
  });

  if (
    ((fieldSelector === undefined && updatedButtons?.length === 0) ||
      (updatedButtons?.length === 0 &&
        ((fieldSelector?.toLowerCase() === 'conditions' &&
          Array.isArray(conditionButtons) &&
          conditionButtons?.length === 0) ||
          (fieldSelector?.toLowerCase() === 'treatments' &&
            Array.isArray(treatmentsButtons) &&
            treatmentsButtons?.length === 0)))) &&
    !isEditing
  ) {
    return <></>;
  }

  return (
    <>
      <ComponentWrapper
        params={props.params}
        isSidenavPart={props?.isSidenavPart}
        className={base()}
      >
        <div className={`${wrapper()}`}>
          {(props?.fields?.title?.value !== '' || isEditing) &&
            (() => {
              const HeadingTag = props?.isSidenavPart ? 'h3' : 'h2';
              return (
                <HeadingTag
                  className={classNames(
                    title(),
                    props?.isSidenavPart
                      ? 'text-24 leading-28 lg:text-26 lg:leading-31'
                      : 'text-26 leading-31 lg:text-36 lg:leading-43'
                  )}
                >
                  <PlainTextWrapper field={props?.fields?.title} />
                </HeadingTag>
              );
            })()}

          {(props?.fields?.description?.value !== '' || isEditing) &&
            (() => {
              return (
                <p className="mb-16 text-theme-subtext">
                  <PlainTextWrapper field={props?.fields?.description} />
                </p>
              );
            })()}

          <ul className={linkArray()}>
            {isEditing ? (
              <>
                {Array.isArray(buttons) && buttons.length > 0 ? (
                  buttons.map((button, index: number) => {
                    return (
                      <li key={index} className={ctaStyle === 'tertiary' ? 'py-7' : ''}>
                        <LinkWrapper
                          field={button?.fields?.link as LinkField | LinkFieldValue}
                          ctaVariant={ctaStyle}
                          ctaIcon={ctaStyle === 'tertiary' ? 'arrow-right' : undefined}
                          className={
                            ctaStyle === 'tertiary'
                              ? 'w-fit !text-left md:text-center !inline [&>svg]:!ml-4 [&>svg]:inline'
                              : 'w-full md:w-fit'
                          }
                        />
                      </li>
                    );
                  })
                ) : (
                  <></>
                )}
              </>
            ) : (
              <>
                {Array.isArray(updatedButtons) && updatedButtons.length > 0 ? (
                  updatedButtons.map((button, index: number) => {
                    return (
                      <li key={index} className={ctaStyle === 'tertiary' ? 'py-7' : ''}>
                        <LinkWrapper
                          field={button?.fields?.link as LinkField | LinkFieldValue}
                          ctaVariant={ctaStyle}
                          ctaIcon={ctaStyle === 'tertiary' ? 'arrow-right' : undefined}
                          className={
                            ctaStyle === 'tertiary'
                              ? 'w-fit !text-left md:text-center !inline [&>svg]:!ml-4 [&>svg]:inline'
                              : 'w-full md:w-fit'
                          }
                        />
                      </li>
                    );
                  })
                ) : (
                  <>
                    {fieldSelector?.toLowerCase() === 'conditions'
                      ? Array.isArray(conditionButtons) &&
                        conditionButtons?.map((button: ConditionTreatmentButton, index: number) => {
                          return (
                            <li key={index} className={'py-7'}>
                              <LinkWrapper
                                field={{
                                  value: { href: button?.url, text: button?.fields?.Title?.value },
                                }}
                                ctaVariant={'tertiary'}
                                ctaIcon={'arrow-right'}
                                className={
                                  ctaStyle === 'tertiary'
                                    ? 'w-fit !text-left md:text-center !inline [&>svg]:!ml-4 [&>svg]:inline'
                                    : 'w-full md:w-fit'
                                }
                              >
                                {button?.fields?.Title?.value}
                              </LinkWrapper>
                            </li>
                          );
                        })
                      : fieldSelector?.toLowerCase() === 'treatments' &&
                        Array.isArray(treatmentsButtons) &&
                        treatmentsButtons?.map(
                          (button: ConditionTreatmentButton, index: number) => {
                            return (
                              <li key={index} className={'py-7'}>
                                <LinkWrapper
                                  field={{
                                    value: {
                                      href: button?.url,
                                      text: button?.fields?.Title?.value,
                                    },
                                  }}
                                  ctaVariant={'tertiary'}
                                  ctaIcon={'arrow-right'}
                                  className={
                                    ctaStyle === 'tertiary'
                                      ? 'w-fit !text-left md:text-center !inline [&>svg]:!ml-4 [&>svg]:inline'
                                      : 'w-full md:w-fit'
                                  }
                                >
                                  {button?.fields?.Title?.value}
                                </LinkWrapper>
                              </li>
                            );
                          }
                        )}
                  </>
                )}
              </>
            )}
          </ul>
        </div>
      </ComponentWrapper>
    </>
  );
};

export default ButtonArray;

export const TailwindVariants = tv({
  slots: {
    base: 'bg-theme-bg ',
    title: 'font-centuryGothic font-bold mb-4 text-theme-text',
    wrapper: 'max-w-screen-xxl lg:px-20 px-10 mx-auto lg:py-20 py-12',
    linkArray: 'flex flex-col md:flex-row flex-wrap gap-x-16 gap-y-6',
  },
});
